import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multi-select";
import { AppConfigKeyEditor } from "./AppConfigKeyEditor";
import { AppConfiguration, AppConfigKey } from "@/types/application";
import { getAllCustomers } from "@/utils/customerLookup";
import { Plus, AlertCircle, Circle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";

interface AppConfigurationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (config: Omit<AppConfiguration, 'id' | 'createdAt' | 'updatedAt'>) => void;
  applicationId: string;
  editConfig?: AppConfiguration;
}

export const AppConfigurationDialog = ({
  open,
  onOpenChange,
  onSave,
  applicationId,
  editConfig
}: AppConfigurationDialogProps) => {
  const [name, setName] = useState(editConfig?.name || '');
  const [description, setDescription] = useState(editConfig?.description || '');
  const [keys, setKeys] = useState<AppConfigKey[]>(editConfig?.configurationData.keys || []);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>(editConfig?.customers || []);
  const [isActive, setIsActive] = useState(editConfig?.isActive ?? true);
  const [errors, setErrors] = useState<string[]>([]);

  const baseline = useMemo(
    () => ({
      name: editConfig?.name || '',
      description: editConfig?.description || '',
      keys: editConfig?.configurationData.keys || [],
      selectedCustomers: editConfig?.customers || [],
      isActive: editConfig?.isActive ?? true,
    }),
    [editConfig],
  );

  // Reset form when dialog (re)opens.
  useEffect(() => {
    if (open) {
      setName(baseline.name);
      setDescription(baseline.description);
      setKeys(baseline.keys);
      setSelectedCustomers(baseline.selectedCustomers);
      setIsActive(baseline.isActive);
      setErrors([]);
    }
  }, [open, baseline]);

  const resetToBaseline = () => {
    setName(baseline.name);
    setDescription(baseline.description);
    setKeys(baseline.keys);
    setSelectedCustomers(baseline.selectedCustomers);
    setIsActive(baseline.isActive);
    setErrors([]);
  };

  const guard = useUnsavedChangesGuard({
    open,
    current: { name, description, keys, selectedCustomers, isActive },
    baseline,
    onClose: () => onOpenChange(false),
    onDiscard: resetToBaseline,
    surface: "AppConfigurationDialog",
  });

  const customerOptions = getAllCustomers().map((c) => ({ value: c.id, label: c.name }));

  const addKey = () => {
    setKeys([...keys, { key: '', value: '', type: 'string', required: false }]);
  };

  const updateKey = (index: number, updatedKey: AppConfigKey) => {
    const newKeys = [...keys];
    newKeys[index] = updatedKey;
    setKeys(newKeys);
  };

  const removeKey = (index: number) => {
    setKeys(keys.filter((_, i) => i !== index));
  };

  const validate = () => {
    const validationErrors: string[] = [];

    if (!name.trim()) {
      validationErrors.push("Configuration name is required");
    }

    if (keys.length === 0) {
      validationErrors.push("At least one key-value pair is required");
    }

    if (selectedCustomers.length === 0) {
      validationErrors.push("At least one customer must be selected");
    }

    const keyNames = keys.map(k => k.key);
    const duplicateKeys = keyNames.filter((key, index) => keyNames.indexOf(key) !== index);
    if (duplicateKeys.length > 0) {
      validationErrors.push(`Duplicate key names: ${duplicateKeys.join(', ')}`);
    }

    keys.forEach((key, index) => {
      if (!key.key.trim()) {
        validationErrors.push(`Key #${index + 1}: Key name is required`);
      }
      if (!/^[a-zA-Z0-9_]+$/.test(key.key)) {
        validationErrors.push(`Key #${index + 1}: Key name must contain only letters, numbers, and underscores`);
      }
    });

    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    onSave({
      applicationId,
      name: name.trim(),
      description: description.trim() || undefined,
      configurationData: { keys },
      customers: selectedCustomers,
      isActive,
    });

    guard.markClean();
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={guard.requestClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>{editConfig ? 'Edit' : 'Create'} App Configuration</DialogTitle>
              {guard.isDirty && (
                <Badge
                  variant="outline"
                  className="border-warning text-warning gap-1.5 font-normal"
                >
                  <Circle className="h-2 w-2 fill-warning text-warning" />
                  Unsaved changes
                </Badge>
              )}
            </div>
            <DialogDescription>
              Configure managed settings for this application
            </DialogDescription>
          </DialogHeader>

          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            {/* Name and Description */}
            <div className="space-y-4">
              <div>
                <Label>Configuration Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Production Settings"
                  maxLength={100}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this configuration for?"
                  rows={2}
                  maxLength={200}
                />
              </div>
            </div>

            {/* Customer Selection */}
            <div className="space-y-2">
              <Label>Customers *</Label>
              <MultiSelect
                options={customerOptions}
                value={selectedCustomers}
                onChange={setSelectedCustomers}
                placeholder="Select customers"
                emptyMessage="No customers available"
              />
            </div>

            {/* Key-Value Pairs */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Key-Value Pairs *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addKey}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Key
                </Button>
              </div>

              {keys.length === 0 ? (
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    No configuration keys yet. Click "Add Key" to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {keys.map((key, index) => (
                    <AppConfigKeyEditor
                      key={index}
                      configKey={key}
                      onChange={(updatedKey) => updateKey(index, updatedKey)}
                      onRemove={() => removeKey(index)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Active Toggle */}
            <div className="flex items-center space-x-2">
              <Switch checked={isActive} onCheckedChange={setIsActive} />
              <Label>Active</Label>
              <span className="text-xs text-muted-foreground ml-2">
                (Only active configurations are deployed to devices)
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                // Cancel always reverts to the saved baseline so the dialog
                // never closes (or reopens) in a dirty state.
                resetToBaseline();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!guard.isDirty}
              title={!guard.isDirty ? "No changes to save" : undefined}
            >
              {editConfig ? 'Update' : 'Create'} Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={guard.confirmOpen}
        onCancel={guard.cancelDiscard}
        onDiscard={guard.confirmDiscard}
      />
    </>
  );
};
